// store/responseStore.ts
import { defineStore } from 'pinia';

import { Response, ResponseStatus } from '@/types/Response';
import ResponseService from '@/services/ResponseService';

interface ResponseStoreState {
    responses: { [PK: string]: Response };
    uploadProgressByPostId: Record<string, number>;
    processingStartByPostId: Record<string, number>;
}

export const useResponseStore = defineStore('responseStore', {
    state: (): ResponseStoreState => ({
        responses: {},
        uploadProgressByPostId: {},
        processingStartByPostId: {},
    }),
    persist: true,

    actions: {
        // Add multiple challenges to the store by PK

        add(response_list: Response[]) {
            if (Object.keys(this.responses).length > 20000) {
                console.warn('ResponseStore exceeded safe cache size, clearing...');
                this.clear();
            }
            response_list.forEach((response) => {
                if (response.PK) {
                    this.responses[response.PK] = { ...response };
                    // this.responses[response.PK] = response;
                }
            });
        },
        // Get a single challenge by its PK
        get(PK: string): Response | undefined {
            return this.responses[PK];
        },

        getByChallengeId(challengeId: string): Response[] {
            return Object.values(this.responses).filter((r) => r.challenge_id === challengeId);
        },

        async_get_and_set: async function (
            PK: string,
            force = false
        ): Promise<Response | undefined> {
            if (Object.keys(this.responses).length > 20000) {
                console.warn('ResponseStore exceeded safe cache size, clearing...');
                this.clear();
            }
            const response = await ResponseService.getResponseById(PK);
            if (response) {
                this.add([response]);
                return response;
            }
            if (this.responses[PK] && !force) {
                return this.responses[PK];
            }

            console.warn(`No response found for PK: ${PK}`);
            return undefined;
        },

        // Remove a challenge by its PK
        remove(PK: string) {
            if (this.responses[PK]) {
                delete this.responses[PK];
            }
        },

        // Clear the entire challenge cache
        clear() {
            this.responses = {};
        },

        // Update specific properties of a challenge (e.g., like or comment counts)
        update(PK: string, updates: Partial<Response>) {
            if (this.responses[PK]) {
                const filteredUpdates = Object.fromEntries(
                    Object.entries(updates).filter(
                        ([_, v]) => v !== '' && v !== undefined && v !== null
                    )
                );

                this.responses[PK] = { ...this.responses[PK], ...filteredUpdates };
                // this.responses[PK] = { ...this.responses[PK], ...updates };
            } else {
                console.warn(`Response with PK ${PK} not found in store.`);
            }
            console.log('updating response', this.responses[PK]);
            ResponseService.updateResponse(this.responses[PK]).catch((error) => {
                console.error(`Failed to update response ${PK}:`, error);
            });
        },

        async publish(PK: string) {
            const response = this.responses[PK];
            if (!response) {
                console.warn(`Cannot publish. No response found for PK: ${PK}`);
                return;
            }

            response.status = ResponseStatus.PUBLISHED;
            this.responses[PK] = response;

            try {
                // await ResponseService.updateResponse(response);
                await ResponseService.updateResponse({
                    ...response,
                    write_tags: true, // or response.tags if you want to be explicit
                });
            } catch (error) {
                console.error(`Failed to publish response ${PK}`, error);
            }
        },

        setUploadProgress(postId: string, progress: number) {
            if (!postId.startsWith('POST#')) {
                postId = `POST#${postId}`;
            }
            this.uploadProgressByPostId[postId] = progress;

            if (progress >= 100 && !this.processingStartByPostId[postId]) {
                this.processingStartByPostId[postId] = Date.now();
            }
        },

        // Get upload progress for a post
        getUploadProgress(postId: string): number {
            if (!postId.startsWith('POST#')) {
                postId = `POST#${postId}`;
            }
            return this.uploadProgressByPostId[postId] || 0;
        },

        setProcessingStart(postId: string) {
            if (!postId.startsWith('POST#')) {
                postId = `POST#${postId}`;
            }
            if (!this.processingStartByPostId[postId]) {
                this.processingStartByPostId[postId] = Date.now();
            }
        },

        getProcessingStart(postId: string): number {
            if (!postId.startsWith('POST#')) {
                postId = `POST#${postId}`;
            }
            return this.processingStartByPostId[postId] ?? 0;
        },

        clearUploadProgress(postId: string) {
            delete this.uploadProgressByPostId[postId];
            delete this.processingStartByPostId[postId];
        },
    },
});
